import { useState, useRef, useEffect, useCallback } from "react";
import { Copy, Trash2, Plus, Power, PowerOff, RefreshCw, Upload, Pen, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric"
  });
};

/* ======== SIGNATURE MANAGER ======== */
const SignatureManager = ({ getAuthHeader }) => {
  const [signature, setSignature] = useState("");
  const [mode, setMode] = useState(null); // null, 'draw', 'upload'
  const sigCanvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchSignature = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/signature`, getAuthHeader());
      setSignature(res.data.signature_data || "");
    } catch {
      // silently fail - signature is optional
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchSignature(); }, [fetchSignature]);

  const handleSaveDrawn = async () => {
    if (sigCanvasRef.current?.isEmpty()) {
      toast.error("Please draw your signature first");
      return;
    }
    const dataUrl = sigCanvasRef.current.toDataURL("image/png");
    try {
      await axios.post(`${API}/admin/signature`, { signature_data: dataUrl }, getAuthHeader());
      setSignature(dataUrl);
      setMode(null);
      toast.success("Signature saved");
    } catch { toast.error("Failed to save signature"); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a PNG or image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      try {
        await axios.post(`${API}/admin/signature`, { signature_data: dataUrl }, getAuthHeader());
        setSignature(dataUrl);
        setMode(null);
        toast.success("Signature uploaded");
      } catch { toast.error("Failed to upload signature"); }
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = async () => {
    try {
      await axios.delete(`${API}/admin/signature`, getAuthHeader());
      setSignature("");
      toast.success("Signature removed");
    } catch { toast.error("Failed to remove signature"); }
  };

  return (
    <div className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-medium text-gray-900">Director Signature</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {signature ? "Your signature will appear on all certificates." : "Upload or draw your signature to appear on certificates."}
          </p>
        </div>
        {!mode && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setMode("draw")} data-testid="sig-draw-btn" className="gap-2">
              <Pen className="w-4 h-4" />Draw
            </Button>
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="sig-upload-btn" className="gap-2">
              <Upload className="w-4 h-4" />Upload PNG
            </Button>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
          </div>
        )}
      </div>

      {/* Current signature preview */}
      {signature && !mode && (
        <div className="flex items-center gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-3 inline-block">
            <img src={signature} alt="Director signature" className="h-16 object-contain" data-testid="sig-preview" />
          </div>
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={handleRemove} data-testid="sig-remove-btn">
            <Trash2 className="w-4 h-4 mr-1" />Remove
          </Button>
        </div>
      )}

      {/* Draw mode */}
      {mode === "draw" && (
        <div>
          <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg overflow-hidden mb-3">
            <SignatureCanvas
              ref={sigCanvasRef}
              penColor="black"
              canvasProps={{
                width: 500,
                height: 150,
                className: "w-full",
                "data-testid": "sig-canvas"
              }}
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSaveDrawn} data-testid="sig-save-drawn-btn" className="bg-warm-700 hover:bg-warm-800 text-white">Save Signature</Button>
            <Button size="sm" variant="outline" onClick={() => sigCanvasRef.current?.clear()}>Clear</Button>
            <Button size="sm" variant="ghost" onClick={() => setMode(null)}><X className="w-4 h-4 mr-1" />Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ======== MAIN PANEL ======== */
export const CertificatesPanel = ({
  certificates, certPageEnabled, onTogglePage, onCreateCert, onDeleteCert, onRefresh, isLoading, getAuthHeader
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    participant_name: "",
    program_name: "Parenting Support Program",
    total_hours: "12 hours over 6 weeks",
    completion_date: "",
    case_number: "",
    instructor_name: "The Pillar Project",
    instructor_title: "Director",
    code: "",
    certificate_number: ""
  });

  const resetForm = () => setForm({
    participant_name: "", program_name: "Parenting Support Program",
    total_hours: "12 hours over 6 weeks", completion_date: "", case_number: "",
    instructor_name: "The Pillar Project", instructor_title: "Director",
    code: "", certificate_number: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await onCreateCert(form);
    if (success) { setDialogOpen(false); resetForm(); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied to clipboard");
  };

  return (
    <div>
      {/* Signature Manager */}
      <SignatureManager getAuthHeader={getAuthHeader} />

      {/* Certificate Page Toggle */}
      <div className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-900">Certificate Page Status</h3>
          <p className="text-sm text-gray-500 mt-1">
            {certPageEnabled
              ? "The certificate page is visible in navigation and accessible to participants."
              : "The certificate page is hidden from navigation and disabled."}
          </p>
        </div>
        <Button onClick={onTogglePage} data-testid="toggle-cert-page-btn"
          variant={certPageEnabled ? "default" : "outline"}
          className={certPageEnabled ? "bg-green-600 hover:bg-green-700 gap-2" : "border-gray-300 gap-2"}>
          {certPageEnabled ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
          {certPageEnabled ? "Enabled" : "Disabled"}
        </Button>
      </div>

      {/* Action bar */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-gray-500">{certificates.length} certificate{certificates.length !== 1 ? "s" : ""} issued</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isLoading} className="gap-2">
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button onClick={() => setDialogOpen(true)} data-testid="create-cert-btn" className="bg-warm-700 hover:bg-warm-800 text-white gap-2">
            <Plus className="w-4 h-4" />Create Certificate
          </Button>
        </div>
      </div>

      {/* Certificates table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Access Code</TableHead>
              <TableHead>Participant</TableHead>
              <TableHead>Case #</TableHead>
              <TableHead>Program</TableHead>
              <TableHead>Completed</TableHead>
              <TableHead>Cert #</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {certificates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  No certificates issued yet. Click "Create Certificate" to generate one.
                </TableCell>
              </TableRow>
            ) : (
              certificates.map((cert) => (
                <TableRow key={cert.id} data-testid={`cert-row-${cert.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="bg-warm-50 text-warm-800 px-2 py-1 rounded font-mono text-sm font-bold">{cert.code}</code>
                      <button onClick={() => copyCode(cert.code)} className="text-gray-400 hover:text-warm-700 transition-colors" title="Copy code">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{cert.participant_name}</TableCell>
                  <TableCell className="text-sm text-gray-500">{cert.case_number || "—"}</TableCell>
                  <TableCell className="text-sm max-w-[180px] truncate">{cert.program_name}</TableCell>
                  <TableCell>{formatDate(cert.completion_date)}</TableCell>
                  <TableCell className="text-xs font-mono text-gray-500">{cert.certificate_number}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => onDeleteCert(cert.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create certificate dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Completion Certificate</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="participant_name">Participant Full Legal Name *</Label>
              <Input id="participant_name" required data-testid="cert-participant-name"
                value={form.participant_name}
                onChange={(e) => setForm({ ...form, participant_name: e.target.value })}
                placeholder="As it appears on court or DHS case file" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="case_number">Case Number</Label>
              <Input id="case_number" data-testid="cert-case-number"
                value={form.case_number}
                onChange={(e) => setForm({ ...form, case_number: e.target.value })}
                placeholder="Court case or social services ID number" className="mt-1 font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="program_name">Course Title *</Label>
                <Input id="program_name" required data-testid="cert-program-name"
                  value={form.program_name}
                  onChange={(e) => setForm({ ...form, program_name: e.target.value })}
                  className="mt-1" />
              </div>
              <div>
                <Label htmlFor="total_hours">Duration / Hours *</Label>
                <Input id="total_hours" required data-testid="cert-total-hours"
                  value={form.total_hours}
                  onChange={(e) => setForm({ ...form, total_hours: e.target.value })}
                  placeholder="e.g., 12 hours over 6 weeks" className="mt-1" />
              </div>
            </div>
            <div>
              <Label htmlFor="completion_date">Date of Completion *</Label>
              <Input id="completion_date" type="date" required data-testid="cert-completion-date"
                value={form.completion_date}
                onChange={(e) => setForm({ ...form, completion_date: e.target.value })}
                className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="instructor_name">Instructor Name *</Label>
                <Input id="instructor_name" required data-testid="cert-instructor-name"
                  value={form.instructor_name}
                  onChange={(e) => setForm({ ...form, instructor_name: e.target.value })}
                  className="mt-1" />
              </div>
              <div>
                <Label htmlFor="instructor_title">Instructor Title / Credentials *</Label>
                <Input id="instructor_title" required data-testid="cert-instructor-title"
                  value={form.instructor_title}
                  onChange={(e) => setForm({ ...form, instructor_title: e.target.value })}
                  placeholder="e.g., LCSW-C, Director" className="mt-1" />
              </div>
            </div>
            <div className="border-t pt-4">
              <p className="text-xs text-gray-500 font-medium mb-3">IDENTIFIERS (leave blank to auto-generate)</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="code">Access Code</Label>
                  <Input id="code" data-testid="cert-code-input"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="CERT-XXXXXX" className="mt-1 font-mono" />
                </div>
                <div>
                  <Label htmlFor="certificate_number">Certificate Number</Label>
                  <Input id="certificate_number" data-testid="cert-number-input"
                    value={form.certificate_number}
                    onChange={(e) => setForm({ ...form, certificate_number: e.target.value })}
                    placeholder="PPC-2026-XXXX" className="mt-1 font-mono" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="cert-submit-btn" className="bg-warm-700 hover:bg-warm-800">Create Certificate</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
